import { useRef, useState } from 'react'
import type { UploadOutcome } from '@shared/types'
import { MAX_FILES_PER_SEND, MAX_FILE_BYTES, type WebDropTarget } from '@shared/web'
import { sendFiles } from '../lib/api'

interface Props {
  jobId: string
  /** The input these files are for, as the broker described it. */
  target: WebDropTarget
  /** Called after at least one file uploads successfully. */
  onSent: () => void
}

/**
 * Drag-and-drop (or click-to-browse) file intake.
 *
 * The checks here are for a fast, clear message before a round trip — they are
 * not the enforcement. The broker re-checks every name and extension, and the
 * signed upload policy it returns carries the size limit as a condition Cloud
 * Storage applies itself.
 */
export default function DropZone({ jobId, target, onSent }: Props): JSX.Element {
  const { accept, label, help, slotId } = target
  // A labelled zone already says what belongs in it, right above the box.
  // Repeating "Drop Monthly sales export here" inside would be the same
  // sentence twice.
  const prompt = label ? 'Drop files here' : target.prompt
  const inputRef = useRef<HTMLInputElement>(null)
  const [dragging, setDragging] = useState(false)
  const [busy, setBusy] = useState(false)
  const [outcomes, setOutcomes] = useState<UploadOutcome[]>([])
  const [error, setError] = useState('')

  const send = async (files: File[]): Promise<void> => {
    if (files.length === 0) return
    setError('')
    setOutcomes([])
    if (files.length > MAX_FILES_PER_SEND) {
      setError(`Please send up to ${MAX_FILES_PER_SEND} files at a time.`)
      return
    }
    const oversize = files.find((f) => f.size > MAX_FILE_BYTES)
    if (oversize) {
      setError(
        `“${oversize.name}” is too big — files must be under ${MAX_FILE_BYTES / 1024 / 1024} MB.`
      )
      return
    }
    const empty = files.find((f) => f.size === 0)
    if (empty) {
      setError(`“${empty.name}” is empty. Check the file and try again.`)
      return
    }

    setBusy(true)
    try {
      const results = await sendFiles(jobId, files, slotId)
      setOutcomes(results)
      if (results.some((r) => r.ok)) onSent()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Sending files failed.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="drop-slot">
      {label && (
        <div className="drop-slot-head">
          <h3 className="drop-slot-label">
            {label}
            {!target.required && <span className="chip optional">Optional</span>}
          </h3>
          {help && <p className="drop-slot-help">{help}</p>}
        </div>
      )}
      <div
        className={`dropzone${dragging ? ' dragging' : ''}${busy ? ' busy' : ''}`}
        role="button"
        tabIndex={0}
        aria-label={`${prompt}, or press Enter to choose files`}
        onClick={() => !busy && inputRef.current?.click()}
        onKeyDown={(e) => {
          if (!busy && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault()
            inputRef.current?.click()
          }
        }}
        onDragOver={(e) => {
          e.preventDefault()
          if (!busy) setDragging(true)
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault()
          setDragging(false)
          if (!busy) void send(Array.from(e.dataTransfer.files))
        }}
      >
        <div className="dropzone-icon" aria-hidden="true">
          {busy ? '⏳' : '📥'}
        </div>
        <div className="dropzone-prompt">{busy ? 'Sending…' : prompt}</div>
        <div className="dropzone-hint">or click to choose files ({accept.join(', ')})</div>
        <input
          ref={inputRef}
          type="file"
          multiple
          accept={accept.join(',')}
          style={{ display: 'none' }}
          onChange={(e) => {
            const files = Array.from(e.target.files ?? [])
            e.target.value = ''
            void send(files)
          }}
        />
      </div>

      {error && <div className="alert error">{error}</div>}
      {outcomes.length > 0 && (
        <ul className="outcome-list">
          {outcomes.map((o, i) => (
            <li key={i} className={o.ok ? 'ok' : 'bad'}>
              {o.ok ? '✓' : '✕'} {o.name}
              {!o.ok && o.error ? <span className="outcome-error"> — {o.error}</span> : null}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
